import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import ImportErrorType from "@mapmycustomers/shared/enum/imports/ImportErrorType";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import upload from "@app/assets/imports/upload.svg";
import ButtonLink from "@app/component/ButtonLink";
import DropZone from "@app/component/Dropzone";
import { clearFilePreview, selectFile } from "@app/scene/settings/component/Imports/store/actions";
import {
  getErrorCodeWhileUploading,
  getFilePreview,
  isFilePreviewLoading,
} from "@app/scene/settings/component/Imports/store/selectors";
import getErrorTypeMessage from "@app/scene/settings/component/Imports/util/getErrorTypeMessage";
import { RootState } from "@app/store/rootReducer";
import FilePreview from "@app/types/FilePreview";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { IMPORT_FILE_UPLOAD_LIMIT } from "@app/util/consts";

import styles from "./FileUploader.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  clearFilePreview: typeof clearFilePreview;
  errorCode?: ImportErrorType;
  filePreview?: FilePreview;
  fileUploading: boolean;
  selectFile: typeof selectFile.request;
}

const FileUploader: React.FC<Props> = ({
  analyticIssuer,
  clearFilePreview,
  errorCode,
  filePreview,
  fileUploading,
  selectFile,
}) => {
  const intl = useIntl();

  const handleSelectFile = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        const reader = new FileReader();
        reader.readAsText(files[0]);
        reader.onload = () => {
          selectFile(files[0]);
          analyticIssuer?.clicked(["Select File"]);
        };
      }
    },
    [analyticIssuer, selectFile]
  );

  const handleClearFilePreview = useCallback(() => {
    clearFilePreview();
    analyticIssuer?.clicked(["Clear File"]);
  }, [analyticIssuer, clearFilePreview]);

  if (fileUploading) {
    return (
      <div className={styles.dropzone}>
        <LoadingSpinner />
      </div>
    );
  }
  return filePreview || errorCode ? (
    <div className={styles.dropzone}>
      {errorCode ? (
        <FontAwesomeIcon
          className={cn(styles.icon, styles.erroredIcon)}
          icon={faTimesCircle}
          inverse
        />
      ) : (
        <FontAwesomeIcon
          className={cn(styles.icon, styles.successIcon)}
          icon={faCheckCircle}
          inverse
        />
      )}

      <div className={styles.dropzoneTitle}>
        {errorCode ? (
          <div>
            {intl.formatMessage({
              id: "settings.newImports.fileUploader.invalidFile.text",
              defaultMessage: "File invalid",
              description: "Dropzone invalid file text",
            })}
            <div className={styles.errorMessage}>{getErrorTypeMessage(intl, errorCode)}</div>
          </div>
        ) : (
          intl.formatMessage({
            id: "settings.newImports.fileUploader.dropzoneTitleUploaded",
            defaultMessage: "Import file selected",
            description: "Dropzone uploaded title",
          })
        )}
      </div>
      <p className={styles.dropzoneFileName}>{filePreview?.file.name}</p>
      <Button onClick={handleClearFilePreview} type="link">
        {intl.formatMessage({
          id: "settings.newImports.fileUploader.clearFilePreview",
          defaultMessage: "Remove file",
          description: "Dropzone remove file",
        })}
      </Button>
    </div>
  ) : (
    <div>
      <DropZone accept=".csv" maxSize={IMPORT_FILE_UPLOAD_LIMIT} onSelect={handleSelectFile}>
        <div className={styles.dropzone}>
          <img alt="dropzone icon" className={styles.dropzoneIcon} src={upload} />
          <p className={styles.dropzoneTitle}>
            {intl.formatMessage(
              {
                id: "settings.newImports.fileUploader.dropzoneTitle",
                defaultMessage: "To import, drag and drop a file or <a>upload</a>",
                description: "Dropzone title",
              },
              {
                a: (text: string) => <ButtonLink>{text}</ButtonLink>,
              }
            )}
          </p>
          <p className={styles.dropzoneSubTitle}>
            {intl.formatMessage(
              {
                id: "settings.newImports.fileUploader.dropzoneSubTitle",
                defaultMessage: "We support .csv files up to {limit, number, ::K}MB",
                description: "Dropzone sub title",
              },
              {
                limit: IMPORT_FILE_UPLOAD_LIMIT / (1024 * 1024),
              }
            )}
          </p>
        </div>
      </DropZone>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  errorCode: getErrorCodeWhileUploading(state),
  filePreview: getFilePreview(state),
  fileUploading: isFilePreviewLoading(state),
});

const mapDispatchToProps = {
  clearFilePreview,
  selectFile: selectFile.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileUploader);
